import { useSignal } from "@preact/signals";
import type { ComponentChildren, JSX } from "preact";
import { IcoEye, IcoEyeOff } from "../icons";
import { FieldError } from "./field-error";
import { clearFieldError, useFormErrors } from "./form";

type PasswordProps = Omit<JSX.IntrinsicElements["input"], "type"> & {
  label: ComponentChildren;
  aside?: ComponentChildren;
  error?: string;
};

export function Password({
  label,
  aside,
  error: errorProp,
  class: className,
  name,
  onInput,
  ...props
}: PasswordProps) {
  const visible = useSignal(false);
  const formErrors = useFormErrors();
  const fieldName = typeof name === "string" ? name : undefined;
  const error =
    errorProp ?? (fieldName ? formErrors?.value[fieldName] : undefined);

  return (
    <label class="flex flex-col gap-1">
      <span class="flex justify-between items-center text-sm">
        <span class="font-medium text-text-secondary">{label}</span>
        {aside}
      </span>
      <div class="relative">
        <input
          type={visible.value ? "text" : "password"}
          name={name}
          aria-invalid={!!error}
          onInput={(e) => {
            clearFieldError(formErrors, fieldName);
            onInput?.(e);
          }}
          class={`block w-full rounded-lg border border-border px-3 py-2 pr-10 bg-surface text-text placeholder:text-text-subtle hover:border-text-subtle focus:ring-ring transition-colors ${className || ""}`}
          {...props}
        />
        <button
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-text-subtle hover:text-text-muted"
          onClick={() => {
            visible.value = !visible.value;
          }}
          aria-label={visible.value ? "Hide password" : "Show password"}
        >
          {visible.value ? (
            <IcoEyeOff class="size-5" />
          ) : (
            <IcoEye class="size-5" />
          )}
        </button>
      </div>
      <FieldError>{error}</FieldError>
    </label>
  );
}
